import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../../services/hrmsService";
import { hasHrFeature } from "../../../constants/hrPermissions";
import { FormDrawer, FormFields, useMasterOptions, withNulls } from "../hrForm";
import EmployeeSelect from "../EmployeeSelect";
import { buildPatternPayload, describePattern, emptyWeek, patternToForm, WEEKDAYS } from "../timeHelpers";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton, Tabs } from "../hrComponents";
import { useHr } from "../HrGate";
import { cardStyle, formatDate, hhmm, iconBtnStyle, inputStyle, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "../hrUi";

// Time setup: shifts, weekly / rotating patterns and who follows them, sites, the attendance and overtime policies,
// the attendance lock, and (with the schedule-bridge add-on) the one-time import of the booking shifts.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const Table = ({ heads, children }) => (
  <div style={{ ...cardStyle, overflowX: "auto" }}>
    <table className="table mb-0" style={{ minWidth: 560 }}>
      <thead>
        <tr>
          {heads.map((h) => (
            <th key={h} style={thStyle}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

/* ------------------------------ shifts ------------------------------ */

const shiftFields = (creating) => [
  { key: "code", label: "Code", required: true, max: 16, disabled: !creating, hint: creating ? "Letters, digits and underscores, e.g. DAY" : "The code cannot change" },
  { key: "name", label: "Name", required: true, max: 100 },
  { key: "start_time", label: "Starts", type: "time", required: true },
  { key: "end_time", label: "Ends", type: "time", required: true, hint: "An end at or before the start means the shift runs past midnight." },
  { key: "break_minutes", label: "Unpaid break (minutes)", type: "number", min: 0 },
  { key: "grace_in_min", label: "Late-in grace (minutes)", type: "number", min: 0 },
  { key: "grace_out_min", label: "Early-out grace (minutes)", type: "number", min: 0 },
  { key: "full_day_min", label: "Minutes for a full day", type: "number", hint: "Leave blank to use the shift length less the break and grace." },
  { key: "half_day_min", label: "Minutes for a half day", type: "number" },
  { key: "ot_eligible", label: "Overtime counts on this shift", type: "checkbox" },
  { key: "is_night", label: "This is a night shift", type: "checkbox" },
  { key: "color", label: "Colour (#rrggbb)", max: 9 },
];

function Shifts() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listShifts(true), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  const keys = shiftFields(false).map((f) => f.key).filter((k) => k !== "code");
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add shift</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No shifts yet" text="Add the shifts your people work, then build patterns from them." />
        ) : (
          <Table heads={["Shift", "Hours", "Worked", "Grace", "Status", ""]}>
            {rows.map((s) => (
              <tr key={s.id}>
                <td style={tdStyle}>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: s.color || "#3b82f6", borderRadius: 2, marginRight: 8 }} />
                  <strong>{s.code}</strong> {s.name}
                </td>
                <td style={tdStyle}>
                  {hhmm(s.start_time)}-{hhmm(s.end_time)}
                </td>
                <td style={tdStyle}>{Math.round((s.worked / 60) * 10) / 10}h</td>
                <td style={tdStyle}>
                  {s.grace_in_min}/{s.grace_out_min} min
                </td>
                <td style={tdStyle}>
                  <span style={statusBadge(s.status)}>{statusLabel(s.status)}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button type="button" style={linkBtn} onClick={() => setEdit(s)}>
                    Edit
                  </button>{" "}
                  <button type="button" style={linkBtn} onClick={async () => { const r = await hrmsService.saveShift(s.id, { status: s.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); if (r?.success) reload(); else toast.error(hrmsService.responseMessage(r)); }}>
                    {s.status === "ACTIVE" ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit shift" : "Add shift"}
        columns={2}
        width={620}
        fields={shiftFields(!edit)}
        initial={edit ? { ...edit, start_time: hhmm(edit.start_time), end_time: hhmm(edit.end_time), ot_eligible: !!edit.ot_eligible, is_night: !!edit.is_night } : { break_minutes: 0, grace_in_min: 0, grace_out_min: 0, ot_eligible: true, color: "#3b82f6" }}
        onHide={() => setEdit(undefined)}
        onSave={(clean, raw) => (edit ? hrmsService.saveShift(edit.id, withNulls(raw, keys)) : hrmsService.saveShift(null, clean))}
        onDone={reload}
      />
    </>
  );
}

/* ------------------------------ patterns ------------------------------ */

const PATTERN_TYPES = [
  { label: "Weekly (same days each week, or alternating weeks)", value: "FIXED_WEEKLY" },
  { label: "Rotating (for example 2 weeks morning, 2 weeks night)", value: "ROTATING" },
  { label: "On / off (for example 28 days on, 28 off)", value: "ON_OFF" },
];

function PatternDrawer({ pattern, show, shifts, onHide, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show) setForm(pattern ? patternToForm(pattern) : { name: "", pattern_type: "FIXED_WEEKLY", weeks: [emptyWeek()], blocks: [{ shift_id: null, days: 14 }], on_days: 6, off_days: 1, shift_id: null });
  }, [show, pattern]);
  if (!form) return null;
  const options = [{ label: "Day off", value: "OFF" }, ...shifts.map((s) => ({ label: `${s.code} · ${hhmm(s.start_time)}-${hhmm(s.end_time)}`, value: s.id }))];
  const norm = (v) => (v === "OFF" ? null : v);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.savePattern(pattern && pattern.id, buildPatternPayload(form));
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={pattern ? "Edit pattern" : "New pattern"}
      width={620}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !form.name.trim()}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Name" required>
        <input className="form-control" style={inputStyle} maxLength={100} value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Kind">
        <AppDropdown value={form.pattern_type} options={PATTERN_TYPES} onChange={(e) => set({ pattern_type: e.value })} disabled={!!pattern} />
      </Field>

      {form.pattern_type === "FIXED_WEEKLY" ? (
        <>
          {form.weeks.map((w, i) => (
            <div key={i} style={{ ...cardStyle, padding: 14, marginBottom: 12, border: "1px solid #E5E7EB", boxShadow: "none" }}>
              <div className="d-flex justify-content-between mb-2">
                <strong>{form.weeks.length > 1 ? `Week ${i + 1}` : "Every week"}</strong>
                {form.weeks.length > 1 ? (
                  <button type="button" style={linkBtn} onClick={() => set({ weeks: form.weeks.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                ) : null}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 16px" }}>
                {WEEKDAYS.map(([d, label]) => (
                  <Field key={d} label={label}>
                    <AppDropdown value={w[d] || "OFF"} options={options} onChange={(e) => set({ weeks: form.weeks.map((x, j) => (j === i ? { ...x, [d]: norm(e.value) } : x)) })} />
                  </Field>
                ))}
              </div>
            </div>
          ))}
          {form.weeks.length < 4 ? (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => set({ weeks: [...form.weeks, emptyWeek()] })}>
              Add an alternate week
            </button>
          ) : null}
        </>
      ) : null}

      {form.pattern_type === "ROTATING" ? (
        <>
          {form.blocks.map((b, i) => (
            <div key={i} className="d-flex gap-2 align-items-end mb-2">
              <div style={{ flex: 1 }}>
                <Field label={`Block ${i + 1}`}>
                  <AppDropdown value={b.shift_id || "OFF"} options={options} onChange={(e) => set({ blocks: form.blocks.map((x, j) => (j === i ? { ...x, shift_id: norm(e.value) } : x)) })} />
                </Field>
              </div>
              <div style={{ width: 110 }}>
                <Field label="Days">
                  <input type="number" min={1} className="form-control" style={inputStyle} value={b.days} onChange={(e) => set({ blocks: form.blocks.map((x, j) => (j === i ? { ...x, days: e.target.value } : x)) })} />
                </Field>
              </div>
              {form.blocks.length > 1 ? (
                <button type="button" style={{ ...linkBtn, marginBottom: 20 }} onClick={() => set({ blocks: form.blocks.filter((_, j) => j !== i) })}>
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => set({ blocks: [...form.blocks, { shift_id: null, days: 7 }] })}>
            Add a block
          </button>
        </>
      ) : null}

      {form.pattern_type === "ON_OFF" ? (
        <FormFields
          columns={2}
          values={form}
          onChange={(k, v) => set({ [k]: v === "OFF" ? null : v })}
          fields={[
            { key: "on_days", label: "Days on", type: "number", min: 1 },
            { key: "off_days", label: "Days off", type: "number", min: 1 },
            { key: "shift_id", label: "Shift on the days on", type: "select", options: shifts.map((s) => ({ label: `${s.code} · ${hhmm(s.start_time)}-${hhmm(s.end_time)}`, value: s.id })) },
          ]}
        />
      ) : null}
      {pattern ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 12 }}>Changing a pattern does not change days already generated. Use Generate on the roster board to apply it.</div> : null}
    </SlidePanel>
  );
}

function AssignDrawer({ show, patterns, onHide, onDone }) {
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show) {
      setPeople([]);
      setPick(null);
      setValues({ start_date: todayYmd() });
    }
  }, [show]);
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.assignPattern({ employee_ids: people.map((p) => p.id), pattern_id: values.pattern_id, start_date: values.start_date, end_date: values.end_date || undefined, anchor_date: values.anchor_date || undefined });
      if (res?.success) {
        toast.success(res.message || "Assigned");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Put people on a pattern"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !people.length || !values.pattern_id || !values.start_date}>
            Assign {people.length || ""}
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          { key: "pattern_id", label: "Pattern", type: "select", required: true, options: patterns.map((p) => ({ label: p.name, value: p.id })) },
          { key: "start_date", label: "From", type: "date", required: true },
          { key: "end_date", label: "Until (optional)", type: "date" },
          { key: "anchor_date", label: "Rotation starts on (optional)", type: "date", hint: "Day 1 of the cycle. Defaults to the start date." },
        ]}
      />
      <Field label="Add employees">
        <EmployeeSelect value={pick} exclude={people.map((p) => p.id)} onChange={(id, label) => { if (id) setPeople((ps) => [...ps, { id, label: label || "Employee" }]); setPick(null); }} />
      </Field>
      {people.map((p) => (
        <div key={p.id} className="d-flex justify-content-between py-1" style={{ fontSize: 14 }}>
          <span>{p.label}</span>
          <button type="button" style={linkBtn} onClick={() => setPeople((ps) => ps.filter((x) => x.id !== p.id))}>
            Remove
          </button>
        </div>
      ))}
      <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>An earlier open-ended pattern ends the day before this one starts.</div>
    </SlidePanel>
  );
}

function Patterns() {
  const pats = useLoad(() => hrmsService.listPatterns(true), []);
  const shifts = useLoad(() => hrmsService.listShifts(false), []);
  const assigns = useLoad(() => hrmsService.listPatternAssignments(), []);
  const [edit, setEdit] = useState(undefined);
  const [assigning, setAssigning] = useState(false);
  const shiftList = useMemo(() => shifts.data || [], [shifts.data]);
  const byId = useMemo(() => new Map(shiftList.map((s) => [s.id, s])), [shiftList]);
  const rows = pats.data || [];
  const end = async (a) => {
    const res = await hrmsService.endPatternAssignment(a.id, todayYmd());
    if (res?.success) assigns.reload();
    else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>New pattern</PrimaryButton>
        <SecondaryButton onClick={() => setAssigning(true)} disabled={!rows.length}>
          Put people on a pattern
        </SecondaryButton>
      </div>
      <LoadState loading={pats.loading} error={pats.error} onRetry={pats.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No patterns yet" text="A pattern says which shift a person works on which day, and repeats." />
        ) : (
          <Table heads={["Pattern", "What it does", "People", ""]}>
            {rows.map((p) => (
              <tr key={p.id}>
                <td style={tdStyle}>{p.name}</td>
                <td style={tdStyle}>{describePattern(p, byId)}</td>
                <td style={tdStyle}>{p.assigned}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <button type="button" style={linkBtn} onClick={() => setEdit(p)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>

      <h6 className="mt-4 mb-2">Who follows what</h6>
      <LoadState loading={assigns.loading} error={assigns.error} onRetry={assigns.reload} rows={2}>
        {(assigns.data || []).length === 0 ? (
          <EmptyState title="Nobody is on a pattern yet" />
        ) : (
          <Table heads={["Employee", "Pattern", "From", "Until", ""]}>
            {assigns.data.map((a) => (
              <tr key={a.id}>
                <td style={tdStyle}>{a.full_name}</td>
                <td style={tdStyle}>{a.pattern_name}</td>
                <td style={tdStyle}>{formatDate(a.start_date)}</td>
                <td style={tdStyle}>{a.end_date ? formatDate(a.end_date) : "Open"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <button type="button" style={linkBtn} onClick={() => end(a)}>
                    End today
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>
      <PatternDrawer show={edit !== undefined} pattern={edit || null} shifts={shiftList} onHide={() => setEdit(undefined)} onDone={() => { pats.reload(); }} />
      <AssignDrawer show={assigning} patterns={rows.filter((p) => p.status === "ACTIVE")} onHide={() => setAssigning(false)} onDone={() => { pats.reload(); assigns.reload(); }} />
    </>
  );
}

/* ------------------------------ site assignments ------------------------------ */

function Sites() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listSiteAssignments(), []);
  const options = useMasterOptions(["sites"]);
  const [adding, setAdding] = useState(false);
  const [warn, setWarn] = useState(null);
  const rows = data || [];
  const end = async (a) => {
    const res = await hrmsService.endSiteAssignment(a.id, todayYmd());
    if (res?.success) reload();
    else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Assign to a site</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="Nobody is assigned to a site" text="A site's geofence then applies to that person's mobile punches." />
        ) : (
          <Table heads={["Employee", "Site", "From", "Until", ""]}>
            {rows.map((a) => (
              <tr key={a.id}>
                <td style={tdStyle}>{a.full_name}</td>
                <td style={tdStyle}>{a.site_name}</td>
                <td style={tdStyle}>{formatDate(a.from_date)}</td>
                <td style={tdStyle}>{a.to_date ? formatDate(a.to_date) : "Open"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <button type="button" style={linkBtn} onClick={() => end(a)}>
                    End today
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>
      <FormDrawer
        show={adding}
        title="Assign to a site"
        initial={{ from_date: todayYmd() }}
        fields={[
          { key: "employee_id", label: "Employee", type: "employee", required: true },
          { key: "site_id", label: "Site", type: "select", required: true, options: options.sites || [] },
          { key: "from_date", label: "From", type: "date", required: true },
          { key: "to_date", label: "Until (optional)", type: "date" },
          ...(warn ? [{ key: "override_reason", label: "Reason to assign anyway", type: "textarea", required: true, max: 500, rows: 2, hint: warn }] : []),
        ]}
        onHide={() => { setAdding(false); setWarn(null); }}
        onSave={async (clean) => {
          const res = await hrmsService.assignSite(clean);
          // The server refuses with the rule that was broken; show it and ask for a reason.
          if (res && res.success === false && /Give a reason to assign anyway/.test(res.message || "")) setWarn(res.message);
          return res;
        }}
        onDone={reload}
      />
    </>
  );
}

/* ------------------------------ attendance policy ------------------------------ */

const policyFields = [
  { key: "name", label: "Policy name", required: true, max: 100 },
  { key: "allow_mobile", label: "Allow punching from the mobile app", type: "checkbox" },
  { key: "allow_web", label: "Allow punching from the web", type: "checkbox" },
  { key: "web_ip_allowlist", label: "Web punches only from these IP addresses", max: 1000, hint: "Comma-separated. Leave blank to allow any network." },
  { key: "geofence_required", label: "Reject a mobile punch outside the site or office", type: "checkbox" },
  { key: "block_mock_location", label: "Reject punches from a mock location", type: "checkbox" },
  { key: "late_marks_per_half_day", label: "Late marks that make half a day", type: "number", min: 1, hint: "For example 3. Leave blank for no deduction." },
  { key: "permission_minutes_per_month", label: "Lateness forgiven each month (minutes)", type: "number", min: 0 },
  { key: "regularise_backdate_days", label: "Corrections can go back (days)", type: "number", min: 0 },
  { key: "regularise_monthly_limit", label: "Corrections allowed per month", type: "number", min: 0, hint: "Blank = no limit." },
  { key: "absence_lop_after_days", label: "An unregularised absence becomes loss of pay after (days)", type: "number", min: 1, hint: "Blank = never automatically." },
  { key: "absconding_after_days", label: "Alert HR after this many absent working days in a row", type: "number", min: 1 },
];

// UAE only: Ramadan working hours and the summer midday break. Shown only to AE tenants - they mean nothing elsewhere.
const uaeSeasonalFields = [
  { key: "ramadan_from", label: "Ramadan hours from", type: "date" },
  { key: "ramadan_to", label: "Ramadan hours to", type: "date" },
  { key: "ramadan_reduce", label: "Working day shortened by (minutes)", type: "number", min: 0 },
  { key: "midday_from", label: "Summer midday break from (MM-DD)", max: 5, hint: "For example 06-15" },
  { key: "midday_to", label: "Summer midday break to (MM-DD)", max: 5, hint: "For example 09-15" },
  { key: "midday_start", label: "Site work not allowed from", type: "time" },
  { key: "midday_end", label: "until", type: "time" },
];
const policyFieldsFor = (isAE) => (isAE ? [...policyFields, ...uaeSeasonalFields] : policyFields);

// The stored policy (rules JSON) -> the flat form, and back.
export function policyToForm(p) {
  const r = p.rules || {};
  return {
    ...p,
    allow_mobile: !!p.allow_mobile,
    allow_web: !!p.allow_web,
    geofence_required: !!p.geofence_required,
    block_mock_location: !!p.block_mock_location,
    ramadan_from: r.ramadan ? r.ramadan.from : "",
    ramadan_to: r.ramadan ? r.ramadan.to : "",
    ramadan_reduce: r.ramadan ? r.ramadan.reduce_minutes : "",
    midday_from: r.midday_break ? r.midday_break.from : "",
    midday_to: r.midday_break ? r.midday_break.to : "",
    midday_start: r.midday_break ? r.midday_break.start : "",
    midday_end: r.midday_break ? r.midday_break.end : "",
  };
}
export function formToPolicy(v) {
  const num = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
  const rules = {};
  if (v.ramadan_from && v.ramadan_to) rules.ramadan = { from: v.ramadan_from, to: v.ramadan_to, reduce_minutes: Number(v.ramadan_reduce) || 0 };
  if (v.midday_from && v.midday_to && v.midday_start && v.midday_end) rules.midday_break = { from: v.midday_from, to: v.midday_to, start: v.midday_start, end: v.midday_end };
  if (v.rules && v.rules.roster) rules.roster = v.rules.roster; // kept as it was
  return {
    name: v.name,
    allow_mobile: !!v.allow_mobile,
    allow_web: !!v.allow_web,
    web_ip_allowlist: v.web_ip_allowlist || null,
    geofence_required: !!v.geofence_required,
    block_mock_location: !!v.block_mock_location,
    late_marks_per_half_day: num(v.late_marks_per_half_day),
    permission_minutes_per_month: num(v.permission_minutes_per_month) || 0,
    regularise_backdate_days: num(v.regularise_backdate_days) === null ? 7 : num(v.regularise_backdate_days),
    regularise_monthly_limit: num(v.regularise_monthly_limit),
    absence_lop_after_days: num(v.absence_lop_after_days),
    absconding_after_days: num(v.absconding_after_days) || 7,
    rules: Object.keys(rules).length ? rules : null,
  };
}

function AttendancePolicy() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listAttendancePolicies(), []);
  const settings = useLoad(() => hrmsService.getSettings(), []);
  const isAE = settings.data?.country_pack === "AE";
  const [edit, setEdit] = useState(undefined);
  const def = (data || []).find((p) => !p.location_id);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(def ? def : null)}>{def ? "Edit the attendance policy" : "Create the attendance policy"}</PrimaryButton>
      </div>
      {def ? (
        <div style={{ ...cardStyle, padding: 18, fontSize: 14 }}>
          <div>
            <strong>{def.name}</strong>
          </div>
          <div style={{ color: "#6B7280", marginTop: 6 }}>
            Punch: {[def.allow_mobile ? "mobile" : null, def.allow_web ? "web" : null].filter(Boolean).join(" and ")}
            {def.geofence_required ? " · geofence required" : ""}
            {def.late_marks_per_half_day ? ` · ${def.late_marks_per_half_day} late marks = half a day` : ""}
            {def.absence_lop_after_days ? ` · absence becomes loss of pay after ${def.absence_lop_after_days} days` : ""}
          </div>
        </div>
      ) : (
        <EmptyState title="No attendance policy yet" text="Without one, people can punch from the web and mobile with no geofence, and there are no late-mark or absence rules." />
      )}
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Attendance policy" : "New attendance policy"}
        width={640}
        fields={policyFieldsFor(isAE)}
        initial={edit ? policyToForm(edit) : { name: "Default", allow_mobile: true, allow_web: true, block_mock_location: true, regularise_backdate_days: 7, absconding_after_days: 7 }}
        onHide={() => setEdit(undefined)}
        onSave={(_c, raw) => hrmsService.saveAttendancePolicy(edit && edit.id, formToPolicy({ ...raw, rules: edit && edit.rules }))}
        onDone={reload}
      />
    </LoadState>
  );
}

/* ------------------------------ overtime policy ------------------------------ */

const otFields = [
  { key: "name", label: "Policy name", required: true, max: 100 },
  { key: "approval_mode", label: "How overtime is approved", type: "select", options: [{ label: "Found from attendance, then approved (post-approval)", value: "POST" }, { label: "Asked for first, then approved (pre-approval)", value: "PRE" }] },
  { key: "min_minutes", label: "Minimum overtime that counts (minutes)", type: "number", min: 0 },
  { key: "rounding_minutes", label: "Round down to blocks of (minutes)", type: "number", min: 1 },
  { key: "day_cap_minutes", label: "Daily cap (minutes)", type: "number", hint: "UAE default 120. Blank = none." },
  { key: "week_cap_minutes", label: "Weekly cap (minutes)", type: "number" },
  { key: "quarter_cap_minutes", label: "Quarterly cap (minutes)", type: "number" },
  { key: "normal_multiplier", label: "Normal day rate (×)", type: "number", step: "0.05" },
  { key: "night_multiplier", label: "Night rate (×)", type: "number", step: "0.05" },
  { key: "weekly_off_multiplier", label: "Weekly-off rate (×)", type: "number", step: "0.05" },
  { key: "holiday_multiplier", label: "Holiday rate (×)", type: "number", step: "0.05" },
  { key: "night_start", label: "Night starts", type: "time" },
  { key: "night_end", label: "Night ends", type: "time" },
  { key: "rate_base", label: "Hourly rate is worked out from", type: "select", options: [{ label: "Basic pay", value: "BASIC" }, { label: "Gross pay", value: "GROSS" }] },
  { key: "compensation", label: "Compensation", type: "select", options: [{ label: "Paid in payroll", value: "PAY" }, { label: "Comp-off for work on weekly offs and holidays", value: "COMP_OFF" }, { label: "Employee chooses", value: "CHOICE" }] },
];

function OvertimePolicy() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getOtPolicy(), []);
  const [edit, setEdit] = useState(false);
  const num = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(true)}>{data ? "Edit the overtime policy" : "Set up overtime"}</PrimaryButton>
      </div>
      {data ? (
        <div style={{ ...cardStyle, padding: 18, fontSize: 14, color: "#374151" }}>
          <strong>{data.name}</strong>
          <div style={{ color: "#6B7280", marginTop: 6 }}>
            {data.approval_mode === "PRE" ? "Asked for first" : "Found from attendance"} · minimum {data.min_minutes} min, blocks of {data.rounding_minutes} · cap {data.day_cap_minutes ? `${data.day_cap_minutes} min a day` : "none"} · {Number(data.normal_multiplier)}× normal, {Number(data.night_multiplier)}× night
          </div>
        </div>
      ) : (
        <EmptyState title="Overtime is not set up" text="Until you do, no overtime is recorded from attendance." />
      )}
      <FormDrawer
        show={edit}
        title="Overtime policy"
        width={620}
        columns={2}
        fields={otFields}
        initial={data ? { ...data, night_start: hhmm(data.night_start), night_end: hhmm(data.night_end) } : { name: "Standard", approval_mode: "POST", min_minutes: 30, rounding_minutes: 15, day_cap_minutes: 120, normal_multiplier: 1.25, night_multiplier: 1.5, weekly_off_multiplier: 1.5, holiday_multiplier: 1.5, night_start: "22:00", night_end: "04:00", rate_base: "BASIC", compensation: "PAY" }}
        onHide={() => setEdit(false)}
        onSave={(_c, r) => hrmsService.saveOtPolicy({ ...r, min_minutes: num(r.min_minutes), rounding_minutes: num(r.rounding_minutes), day_cap_minutes: num(r.day_cap_minutes), week_cap_minutes: num(r.week_cap_minutes), quarter_cap_minutes: num(r.quarter_cap_minutes), normal_multiplier: num(r.normal_multiplier), night_multiplier: num(r.night_multiplier), weekly_off_multiplier: num(r.weekly_off_multiplier), holiday_multiplier: num(r.holiday_multiplier) })}
        onDone={reload}
      />
    </LoadState>
  );
}

/* ------------------------------ lock + import ------------------------------ */

export function Lock() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getAttendanceLock(), []);
  const history = useLoad(() => hrmsService.getAttendanceLockHistory(), []);
  const [locking, setLocking] = useState(false);
  const rows = history.data || [];
  const onDone = () => {
    reload();
    history.reload();
  };
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 18, maxWidth: 560, marginBottom: 16 }}>
        <div style={{ fontSize: 14 }}>{data?.locked_through ? `Attendance is locked up to ${formatDate(data.locked_through)}.` : "Attendance is not locked."}</div>
        <div style={{ fontSize: 12, color: "#6B7280", margin: "8px 0 14px" }}>
          Locking freezes finished days for payroll: no new punches, no corrections, no reprocessing. It cannot be undone - but it can be extended to a later date once those days are over too.
        </div>
        <PrimaryButton onClick={() => setLocking(true)}>{data?.locked_through ? "Extend the lock" : "Lock attendance"}</PrimaryButton>
      </div>
      <LoadState loading={history.loading} error={history.error} onRetry={history.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No lock history yet" />
        ) : (
          <div style={{ ...cardStyle, maxWidth: 560, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Locked up to", "Note", "Done"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{formatDate(r.lock_date)}</td>
                    <td style={tdStyle}>{r.note || "—"}</td>
                    <td style={tdStyle}>{formatDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={locking}
        title="Lock attendance"
        fields={[
          { key: "lock_date", label: "Lock up to and including", type: "date", required: true, hint: "Only days that are over can be locked." },
          { key: "note", label: "Note", max: 200 },
        ]}
        onHide={() => setLocking(false)}
        onSave={(clean) => hrmsService.lockAttendance(clean.lock_date, clean.note)}
        onDone={onDone}
        submitLabel="Lock"
      />
    </LoadState>
  );
}

function ShiftImport() {
  const [start, setStart] = useState(todayYmd());
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const load = async () => {
    setBusy(true);
    setDone(null);
    try {
      const res = await hrmsService.previewShiftImport(start);
      setPlan(res?.success ? res.data : null);
      if (!res?.success) toast.error(hrmsService.responseMessage(res));
    } catch (e) {
      toast.error(e.message || "Could not load the preview");
    } finally {
      setBusy(false);
    }
  };
  const run = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.commitShiftImport(start);
      if (res?.success) {
        setDone(res.data);
        setPlan(null);
        toast.success(res.message || "Imported");
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ maxWidth: 760 }}>
      <p style={{ fontSize: 14 }}>
        Crew already have weekly shifts in the booking schedule. This copies them into HR as shifts and weekly patterns, and puts crew who are employees on their pattern. Nothing in the booking schedule is changed, and running it again never creates duplicates.
      </p>
      <div className="d-flex align-items-end gap-2 mb-3">
        <Field label="Patterns start on" style={{ marginBottom: 0 }}>
          <input type="date" className="form-control" style={inputStyle} value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <SecondaryButton onClick={load} disabled={busy}>
          Preview
        </SecondaryButton>
      </div>
      {done ? (
        <div style={{ background: "#D1FAE5", color: "#065F46", padding: 14, borderRadius: 8, fontSize: 14 }}>
          Imported: {done.shifts_created} shifts, {done.patterns_created} patterns, {done.assigned} people assigned. Skipped {done.skipped_not_employee} who are not employees yet and {done.skipped_already_assigned} who already follow a pattern. Now generate the roster from the roster board.
        </div>
      ) : null}
      {plan ? (
        <>
          <div style={{ fontSize: 14, marginBottom: 10 }}>
            {plan.summary.crew} crew · <strong style={{ color: "#065F46" }}>{plan.summary.will_import} will be imported</strong> · {plan.summary.not_employees} are not employees yet · {plan.summary.already_assigned} already follow a pattern
          </div>
          {plan.warnings.map((w) => (
            <div key={w} style={{ color: "#92400E", fontSize: 13 }}>
              {w}
            </div>
          ))}
          <div className="my-3">
            <strong style={{ fontSize: 13 }}>Shifts</strong>
            {plan.shifts.map((s) => (
              <div key={s.code} style={{ fontSize: 13 }}>
                {s.code} · {s.name}
              </div>
            ))}
            <strong style={{ fontSize: 13, display: "block", marginTop: 10 }}>Patterns</strong>
            {plan.patterns.map((p) => (
              <div key={p.name} style={{ fontSize: 13 }}>
                {p.name} · {p.description}
              </div>
            ))}
          </div>
          <Table heads={["Crew member", "Pattern", "Result"]}>
            {plan.people.map((p) => (
              <tr key={p.user_id}>
                <td style={tdStyle}>{p.name}</td>
                <td style={tdStyle}>{p.pattern || "No shifts"}</td>
                <td style={tdStyle}>{p.will_import ? "Will be imported" : !p.is_employee ? "Enable as an employee first" : p.already_has_pattern ? "Already has a pattern" : "—"}</td>
              </tr>
            ))}
          </Table>
          <div className="mt-3">
            <PrimaryButton onClick={run} disabled={busy || !plan.summary.will_import}>
              Import
            </PrimaryButton>
          </div>
        </>
      ) : null}
    </div>
  );
}

const SECTIONS = [
  { key: "shifts", label: "Shifts" },
  { key: "patterns", label: "Patterns" },
  { key: "sites", label: "Sites" },
  { key: "attendance", label: "Attendance policy" },
  { key: "ot", label: "Overtime policy" },
  { key: "lock", label: "Lock" },
];

export default function TimeSetupTab() {
  const { access } = useHr();
  const [tab, setTab] = useState("shifts");
  const sections = hasHrFeature(access, "SCHEDULE_BRIDGE") ? [...SECTIONS, { key: "import", label: "Import booking shifts" }] : SECTIONS;
  return (
    <>
      <Tabs tabs={sections} active={tab} onChange={setTab} />
      {tab === "shifts" ? <Shifts /> : null}
      {tab === "patterns" ? <Patterns /> : null}
      {tab === "sites" ? <Sites /> : null}
      {tab === "attendance" ? <AttendancePolicy /> : null}
      {tab === "ot" ? <OvertimePolicy /> : null}
      {tab === "lock" ? <Lock /> : null}
      {tab === "import" ? <ShiftImport /> : null}
    </>
  );
}
